import type { Assistant } from "@inrev/assistant";
import type { AssistantConversationId, AssistantMessageId } from "@inrev/common";
import { ClickableText, IconTile, Text } from "@portal/components/primitives";
import { memo } from "react";
import { AdminAssistantCopyAnswer } from "./AdminAssistantCopyAnswer";
import { AdminAssistantFeedback } from "./AdminAssistantFeedback";
import { AdminAssistantMarkdown } from "./AdminAssistantMarkdown";
import { AdminAssistantSources } from "./AdminAssistantSources";
import { AdminAssistantThinking } from "./AdminAssistantThinking";
import {
	type AssistantToolCall,
	assistantInterruptedLabel,
	assistantToolProgress,
	assistantTryAgainLabel,
	assistantWebFindingsWarning,
} from "./assistantCopy";

export type AssistantTranscriptItem = {
	feedback?: Assistant.Dtos.Conversation.MessageFeedback | null;
	feedbackMessageId?: AssistantMessageId;
	/** Non-null on a persisted or live-streaming assistant message whose generation was cut off. */
	interruptedCode?: string | null;
	role: Assistant.Dtos.Conversation.Message["role"];
	/** The web pages this answer's tools returned; see `AdminAssistantMarkdown`. */
	sources?: readonly string[];
	text: string;
	toolCalls?: AssistantToolCall[];
};

const NO_TOOL_CALLS: AssistantToolCall[] = [];

/**
 * One turn. The two roles are shaped differently on purpose: a question is a bubble the reader
 * recognizes as their own, an answer runs the full width behind the agent's mark and carries its
 * own provenance and actions underneath.
 */
const AssistantMessage = ({
	conversationId,
	item,
	onFeedback,
	onRetry,
	streaming,
}: {
	/** The conversation this turn belongs to, which its citations are labeled from. */
	conversationId?: AssistantConversationId;
	item: AssistantTranscriptItem;
	onFeedback?: (input: {
		comment?: string;
		messageId: AssistantMessageId;
		rating: Assistant.Dtos.Feedback.Create.Request["rating"];
	}) => Promise<unknown>;
	/** Called when the user wants to re-send the preceding question after an interrupted turn. */
	onRetry?: () => void;
	streaming?: boolean;
}) => {
	if (item.role === "user") {
		return (
			<div className="flex justify-end" data-testid="assistant-user-message">
				<div className="max-w-[84%] min-w-0 rounded-md bg-surface-neutral px-3 py-2">
					<Text className="whitespace-pre-wrap" color="primary" type="body">
						{item.text}
					</Text>
				</div>
			</div>
		);
	}

	const toolCalls = item.toolCalls ?? NO_TOOL_CALLS;
	const isInterrupted = item.interruptedCode != null;
	const isThinking = streaming === true && item.text === "";
	const showActions = !streaming && item.text !== "" && !isInterrupted;
	const showInterruptedMarker = isInterrupted && item.text !== "";
	const ratingProps =
		item.feedbackMessageId !== undefined && onFeedback !== undefined
			? { messageId: item.feedbackMessageId, onSubmit: onFeedback }
			: undefined;

	return (
		<div className="flex items-start gap-2.5" data-testid="assistant-agent-message">
			<IconTile className="mt-[1px]" filled size="sm" tone="primary" type="sparkles" />
			<div className="flex min-w-0 flex-1 flex-col gap-3">
				{isThinking ? (
					<AdminAssistantThinking progress={assistantToolProgress(toolCalls)} />
				) : (
					<AdminAssistantMarkdown
						conversationId={conversationId}
						sources={item.sources}
						streaming={streaming}
						text={item.text}
					/>
				)}
				{!streaming && item.text !== "" && (item.sources?.length ?? 0) > 0 && (
					<Text color="tertiary" data-testid="assistant-web-findings-warning" type="caption">
						{assistantWebFindingsWarning}
					</Text>
				)}
				<AdminAssistantSources toolCalls={toolCalls} />
				{showInterruptedMarker && (
					<div className="flex items-center gap-2.5" data-testid="assistant-interrupted">
						<Text color="secondary" type="caption">
							{assistantInterruptedLabel}
						</Text>
						{onRetry !== undefined && (
							<ClickableText color="link" onClick={onRetry} type="caption">
								{assistantTryAgainLabel}
							</ClickableText>
						)}
					</div>
				)}
				{showActions &&
					(ratingProps !== undefined ? (
						<AdminAssistantFeedback
							comment={item.feedback?.comment}
							rating={item.feedback?.rating}
							trailing={<AdminAssistantCopyAnswer text={item.text} />}
							{...ratingProps}
						/>
					) : (
						<div className="flex items-center gap-3.5">
							<AdminAssistantCopyAnswer text={item.text} />
						</div>
					))}
			</div>
		</div>
	);
};

/** Memoized, since a streaming turn re-renders the transcript on every token. */
export const AdminAssistantMessage = memo(AssistantMessage);
